import { useRef } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Text,
} from "@chakra-ui/react";
import { Upload } from "../../../../types/registerBranchDataTypes";
import { UploadTile } from "../../../ui";

interface UploadsStepProps {
  savedData: {
    fieldname: string;
    originalname: string;
  }[];
  documents_info: Upload;
  setDocumentsInfo: (data: Upload) => void;
}

const UploadsStep: React.FC<UploadsStepProps> = ({
  savedData,
  documents_info,
  setDocumentsInfo,
}) => {
  const licenseRef = useRef<HTMLInputElement>(null);
  const ownerPassportRef = useRef<HTMLInputElement>(null);
  const ownerEIDRef = useRef<HTMLInputElement>(null);
  const ownerVisaRef = useRef<HTMLInputElement>(null);
  const powerOfAttorneyRef = useRef<HTMLInputElement>(null);
  const vatRef = useRef<HTMLInputElement>(null);

  // Function to handle file upload
  const uploadFiles = async (key: keyof Upload, newFile: File) => {
    // Ensure the key is one that accepts a File
    if (
      key === "license" ||
      key === "owner_pp" ||
      key === "owner_eid" ||
      key === "owner_visa" ||
      key === "power_of_attorney" ||
      key === "vat"
    ) {
      // Create a FormData object
      const formData = new FormData();

      // Update the file for the specific key by assigning the new file
      documents_info[key] = newFile; // Directly set the new file

      // Append the file to the FormData object
      formData.append(key, newFile);

      // set state
      setDocumentsInfo({ ...documents_info });
    } else {
      console.error(`Invalid key for file upload: ${key}`);
    }
  };

  // function to get the the full row in the array with the fieldname
  const getRow = (fieldname: string) => {
    return savedData.find((row) => row.fieldname === fieldname);
  };

  return (
    <Box>
      <Text textStyle="caption" color="text.muted" mb={4}>
        PDF files only
      </Text>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Trade licence</FormLabel>
            <Input
              type="file"
              accept=".pdf"
              display={"none"}
              ref={licenseRef}
              onChange={(e) => {
                if (e.target.files) {
                  // Call the function to upload the files
                  uploadFiles("license", e.target.files[0]);
                }
              }}
            />

            <UploadTile
              hint="PDF"
              label="Upload trade licence"
              fileName={
                documents_info.license
                  ? documents_info.license?.name
                  : getRow("license")?.originalname
              }
              onClick={() => licenseRef.current?.click()}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Owner/partner passport</FormLabel>
            <Input
              type="file"
              accept=".pdf"
              display={"none"}
              ref={ownerPassportRef}
              onChange={(e) => {
                if (e.target.files) {
                  uploadFiles("owner_pp", e.target.files[0]);
                }
              }}
            />

            <UploadTile
              hint="PDF"
              label="Upload passport"
              fileName={
                documents_info.owner_pp
                  ? documents_info.owner_pp?.name
                  : getRow("owner_pp")?.originalname
              }
              onClick={() => ownerPassportRef.current?.click()}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Owner/partner EID</FormLabel>
            <Input
              type="file"
              accept=".pdf"
              display={"none"}
              ref={ownerEIDRef}
              onChange={(e) => {
                if (e.target.files) {
                  uploadFiles("owner_eid", e.target.files[0]);
                }
              }}
            />

            <UploadTile
              hint="PDF"
              label="Upload EID"
              fileName={
                documents_info.owner_eid
                  ? documents_info.owner_eid?.name
                  : getRow("owner_eid")?.originalname
              }
              onClick={() => ownerEIDRef.current?.click()}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Owner visa</FormLabel>
            <Input
              type="file"
              accept=".pdf"
              display={"none"}
              ref={ownerVisaRef}
              onChange={(e) => {
                if (e.target.files) {
                  // Call the function to upload the files
                  uploadFiles("owner_visa", e.target.files[0]);
                }
              }}
            />

            <UploadTile
              hint="PDF"
              label="Upload owner visa"
              fileName={
                documents_info.owner_visa
                  ? documents_info.owner_visa?.name
                  : getRow("owner_visa")?.originalname
              }
              onClick={() => ownerVisaRef.current?.click()}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1} alignSelf="center">
          <Checkbox
            name="hasVatCert"
            // isChecked={documents_info.hasVatCert === "yes" ? true : false}
            onChange={(e) => {
              setDocumentsInfo({
                ...documents_info,
                hasVatCert: e.target.checked ? "yes" : "no",
              });
            }}
          >
            Has a VAT certificate
          </Checkbox>
        </GridItem>

        <GridItem colSpan={1}>
          <Input
            type="file"
            accept=".pdf"
            display={"none"}
            ref={vatRef}
            onChange={(e) => {
              if (e.target.files) {
                uploadFiles("vat", e.target.files[0]);
              }
            }}
          />

          <UploadTile
            hint="PDF"
            label="Upload VAT certificate"
            fileName={
              documents_info.vat
                ? documents_info.vat?.name
                : getRow("vat")?.originalname
            }
            isDisabled={documents_info.hasVatCert !== "yes"}
            onClick={() => {
              if (documents_info.hasVatCert === "yes") {
                vatRef.current?.click();
              }
            }}
          />
        </GridItem>

        <GridItem colSpan={1} alignSelf="center">
          <Checkbox
            name="hasPowerOfAttorney"
            // isChecked={documents_info.hasPowerOfAttorney === "yes" ? true : false}
            onChange={(e) => {
              setDocumentsInfo({
                ...documents_info,
                hasPowerOfAttorney: e.target.checked ? "yes" : "no",
              });
            }}
          >
            Has a power of attorney
          </Checkbox>
        </GridItem>

        <GridItem colSpan={1}>
          <Input
            type="file"
            accept=".pdf"
            display={"none"}
            ref={powerOfAttorneyRef}
            onChange={(e) => {
              if (e.target.files) {
                uploadFiles("power_of_attorney", e.target.files[0]);
              }
            }}
          />

          <UploadTile
            hint="PDF"
            label="Upload power of attorney"
            fileName={
              documents_info.power_of_attorney
                ? documents_info.power_of_attorney?.name
                : getRow("power_of_attorney")?.originalname
            }
            isDisabled={documents_info.hasPowerOfAttorney !== "yes"}
            onClick={() => {
              if (documents_info.hasPowerOfAttorney === "yes") {
                powerOfAttorneyRef.current?.click();
              }
            }}
          />
        </GridItem>
      </Grid>
    </Box>
  );
};

export default UploadsStep;
